import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const REFRESH_INTERVAL = 60000; // Refresh every minute

interface SocialMetrics {
  totalPosts: number;
  totalViews: number;
  totalEngagement: number;
  totalFollowers: number;
  engagementRate: number;
  weeklyChange: {
    posts: number;
    views: number;
    engagement: number;
    followers: number;
  };
  platforms: {
    [key: string]: {
      posts: number;
      views: number;
      engagement: number;
      followers: number;
      engagementRate: number;
    };
  };
}

const defaultMetrics: SocialMetrics = {
  totalPosts: 0,
  totalViews: 0,
  totalEngagement: 0,
  totalFollowers: 0,
  engagementRate: 0,
  weeklyChange: {
    posts: 0,
    views: 0,
    engagement: 0,
    followers: 0
  },
  platforms: {}
};

export function useSocialMetrics() {
  const [metrics, setMetrics] = useState<SocialMetrics>(defaultMetrics);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if online before loading
    if (!navigator.onLine) {
      setError('No internet connection. Please check your network and try again.');
      setLoading(false);
      return;
    }

    loadMetrics();
    
    // Set up auto-refresh
    const interval = setInterval(() => {
      if (navigator.onLine) {
        loadMetrics();
      }
    }, REFRESH_INTERVAL);

    // Listen for online/offline events
    const handleOnline = () => {
      setError(null);
      loadMetrics();
    };

    const handleOffline = () => {
      setError('No internet connection. Please check your network and try again.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check Supabase client is initialized
      if (!supabase) {
        throw new Error('Database client not initialized');
      }

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        throw new Error(`Authentication error: ${userError.message}`);
      }
      
      if (!user) {
        console.log('No authenticated user');
        setMetrics(defaultMetrics);
        return;
      }

      // Get connected social accounts with metrics
      const { data: accounts, error: accountsError } = await supabase
        .from('social_accounts')
        .select('*')
        .eq('connected', true);

      if (accountsError) {
        throw new Error(`Failed to fetch social accounts: ${accountsError.message}`);
      }

      const platformMetrics: { [key: string]: any } = {};
      let totalPosts = 0;
      let totalViews = 0;
      let totalEngagement = 0;
      let totalFollowers = 0;

      accounts?.forEach(account => {
        if (account.metrics) {
          const metrics = account.metrics;
          
          // Platform-specific metrics
          platformMetrics[account.platform] = {
            posts: metrics.tweet_count || metrics.post_count || 0,
            views: metrics.impression_count || metrics.view_count || 0,
            engagement: (metrics.like_count || 0) + (metrics.retweet_count || 0) + (metrics.reply_count || 0),
            followers: metrics.followers_count || 0,
            engagementRate: metrics.engagement_rate || 0
          };
          
          // Add to totals
          totalPosts += platformMetrics[account.platform].posts;
          totalViews += platformMetrics[account.platform].views;
          totalEngagement += platformMetrics[account.platform].engagement;
          totalFollowers += platformMetrics[account.platform].followers;
        }
      });

      // Calculate engagement rate
      const engagementRate = totalViews > 0 
        ? (totalEngagement / totalViews) * 100 
        : 0;

      // Get metrics from a week ago for comparison
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      const { data: oldMetrics, error: oldMetricsError } = await supabase
        .from('social_metrics')
        .select('*')
        .lt('recorded_at', weekAgo.toISOString())
        .order('recorded_at', { ascending: false })
        .limit(1);

      if (oldMetricsError) {
        throw new Error(`Failed to fetch historical metrics: ${oldMetricsError.message}`);
      }

      // Calculate weekly changes
      const weeklyChange = {
        posts: oldMetrics?.[0] ? totalPosts - oldMetrics[0].total_posts : 0,
        views: oldMetrics?.[0] ? totalViews - oldMetrics[0].total_views : 0,
        engagement: oldMetrics?.[0] ? totalEngagement - oldMetrics[0].total_engagement : 0,
        followers: oldMetrics?.[0] ? totalFollowers - oldMetrics[0].total_followers : 0
      };

      const newMetrics = {
        totalPosts,
        totalViews,
        totalEngagement,
        totalFollowers,
        engagementRate,
        weeklyChange,
        platforms: platformMetrics
      };

      setMetrics(newMetrics);

      // Store current metrics
      const { error: insertError } = await supabase
        .from('social_metrics')
        .insert(
          Object.entries(platformMetrics).map(([platform, metrics]) => ({
            user_id: user.id,
            platform,
            total_posts: metrics.posts,
            total_views: metrics.views,
            total_engagement: metrics.engagement,
            total_followers: metrics.followers,
            engagement_rate: metrics.engagementRate,
            recorded_at: new Date().toISOString()
          }))
        );

      if (insertError) {
        throw new Error(`Failed to store metrics: ${insertError.message}`);
      }

    } catch (err) {
      console.error('Error loading social metrics:', err);
      
      // Handle specific error types
      if (!navigator.onLine) {
        setError('No internet connection. Please check your network and try again.');
      } else if (err instanceof Error) {
        if (err.message.includes('Failed to fetch')) {
          setError('Unable to connect to the server. Please try again later.');
        } else if (err.message.includes('Authentication')) {
          setError('Authentication error. Please try logging in again.');
        } else {
          setError(`Failed to load metrics: ${err.message}`);
        }
      } else {
        setError('An unexpected error occurred. Please try again later.');
      }

      // Keep the last valid metrics instead of resetting to defaults
      // This prevents UI flashing when there's an error
    } finally {
      setLoading(false);
    }
  };

  return { metrics, loading, error, refresh: loadMetrics };
}